// Native replacement for the Zoorix Sticky ATC app. Delegates the actual add-to-cart to
// the real product form (syncs quantity, then clicks its real submit button) instead of
// re-implementing cart logic, so it always stays in sync with Dawn's own add-to-cart /
// cart-drawer behavior. Variant dropdowns here proxy clicks into the real variant
// picker's own inputs for the same reason — one source of truth for selection state.
class StickyATC extends HTMLElement {
  connectedCallback() {
    // Re-parent to <body> — a transformed/filtered ancestor anywhere in the product
    // section (Dawn uses transform for scroll-in animations) would otherwise turn this
    // element's position: fixed into position: relative-to-that-ancestor instead of the
    // viewport, which can push it fully out of view.
    if (this.parentElement !== document.body) document.body.appendChild(this);

    this.bar = this.querySelector('.sticky-atc__bar');
    this.confirmation = this.querySelector('.sticky-atc__confirmation');
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';

    this.qtyInput = this.querySelector('.sticky-atc__qty-input');
    this.querySelector('[data-qty-minus]')?.addEventListener('click', () => this.stepQuantity(-1));
    this.querySelector('[data-qty-plus]')?.addEventListener('click', () => this.stepQuantity(1));

    this.querySelectorAll('.sticky-atc__select').forEach((select) => {
      select.addEventListener('change', () => this.selectRealOption(select));
    });

    this.querySelector('.sticky-atc__submit')?.addEventListener('click', () => this.submit());

    this.observeVisibility();
    this.observeVariantChanges();
  }

  stepQuantity(delta) {
    const next = Math.max(1, (parseInt(this.qtyInput.value, 10) || 1) + delta);
    this.qtyInput.value = next;
  }

  selectRealOption(select) {
    const mainProduct = document.getElementById(this.dataset.mainProductId);
    const position = select.dataset.optionPosition;
    const fieldset = mainProduct?.querySelector(`fieldset[data-option-position="${position}"]`);
    if (!fieldset) return;

    const radio = Array.from(fieldset.querySelectorAll('.swatch-input__input, input[type="radio"]')).find(
      (el) => el.value === select.value
    );
    if (radio) {
      radio.click();
      return;
    }
    const option = Array.from(fieldset.querySelectorAll('option')).find((el) => el.value === select.value);
    const realSelect = option?.closest('select');
    if (realSelect) {
      realSelect.value = select.value;
      realSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  submit() {
    const realQuantityInput = document.getElementById(this.dataset.quantityInputId);
    if (realQuantityInput) {
      realQuantityInput.value = this.qtyInput.value;
      realQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const realSubmit = document.getElementById(this.dataset.submitButtonId);
    if (!realSubmit || realSubmit.disabled) return;
    realSubmit.click();
    this.showConfirmation();
  }

  showConfirmation() {
    this.bar.hidden = true;
    this.confirmation.hidden = false;
    clearTimeout(this._confirmTimeout);
    this._confirmTimeout = setTimeout(() => {
      this.bar.hidden = false;
      this.confirmation.hidden = true;
    }, 2000);
  }

  // Shows once the real Add to Cart button has scrolled past the top of the viewport —
  // not just "isIntersecting: false", since that's also true before the button has ever
  // been reached (still below the fold). boundingClientRect.top < 0 is what distinguishes
  // "scrolled past it" from "haven't gotten there yet".
  observeVisibility() {
    const realSubmit = document.getElementById(this.dataset.submitButtonId);
    if (!realSubmit) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('sticky-atc--visible', scrolledPast);
      },
      { threshold: 0 }
    );
    observer.observe(realSubmit);
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return this.moneyFormat.replace(/\{\{\s*amount\s*\}\}/, amount);
  }

  observeVariantChanges() {
    const mainProduct = document.getElementById(this.dataset.mainProductId);
    if (!mainProduct) return;

    const sync = () => {
      const dataEl = mainProduct.querySelector('[data-selected-variant]');
      let variant;
      try {
        variant = JSON.parse(dataEl?.textContent);
      } catch {
        return;
      }
      if (!variant) return;

      const image = this.querySelector('.sticky-atc__image');
      if (image && variant.featured_image?.src) {
        image.src = `${variant.featured_image.src}&width=100`;
      }

      const priceEl = this.querySelector('.sticky-atc__current-price');
      if (priceEl) priceEl.textContent = this.formatMoney(variant.price);

      const compareEl = this.querySelector('.sticky-atc__compare-price');
      const showCompare = variant.compare_at_price > variant.price;
      if (compareEl) {
        compareEl.hidden = !showCompare;
        if (showCompare) compareEl.textContent = this.formatMoney(variant.compare_at_price);
      }
    };

    new MutationObserver(sync).observe(mainProduct, { childList: true, subtree: true });
  }
}

if (!customElements.get('sticky-atc')) customElements.define('sticky-atc', StickyATC);
